/**
 * Redutor puro de eventos de mensagem do OpenCode — separado do App para ser
 * testável unitariamente (referências imutáveis obrigatórias).
 *
 * Contrato real do motor 1.18.33 (capturado em tests/capture_events.py):
 * - `message.updated`      → properties.sessionID + properties.info
 * - `message.part.updated` → properties.sessionID + properties.part
 * - `message.part.delta`   → properties.sessionID/messageID/partID/field/delta
 *
 * IMUTABILIDADE: itens alterados recebem novas referências (objeto e arrays);
 * itens não alterados mantêm a referência original. Nunca mutar `prev`.
 */
import type { OcipEvent, Part, SessionMessage } from "./types";

function minimalPart(sessionID: string, messageID: string, id: string, text: string, type = "text"): Part {
  return { id, sessionID, messageID, type, text };
}

export function applyMessageEvent(prev: SessionMessage[], ev: OcipEvent): SessionMessage[] {
  const p = ev.properties as Record<string, unknown>;
  const sessionID = String((p.sessionID as string) ?? "");
  const copy = [...prev];

  if (ev.type === "message.updated" || ev.type === "message.created") {
    const info = p.info as SessionMessage["info"] | undefined;
    if (!info || typeof info.id !== "string") return prev;
    const idx = copy.findIndex((m) => m.info.id === info.id);
    if (idx === -1) return [...copy, { info, parts: [] }];
    copy[idx] = { ...copy[idx], info: { ...copy[idx].info, ...info } };
    return copy;
  }

  if (ev.type === "message.removed") {
    const id = String(p.info ? (p.info as { id: string }).id : (p.messageID ?? ""));
    return copy.filter((m) => m.info.id !== id);
  }

  const part = p.part as Part | undefined;
  const messageID = String((part?.messageID ?? p.messageID) ?? "");
  const partID = String((part?.id ?? p.partID) ?? "");

  if (ev.type === "message.part.delta") {
    const rawField = String(p.field ?? "text");
    // Deltas de raciocínio chegam como "text" (motor atual) ou "reasoning*"
    // em alguns provedores — em ambos acumulam em part.text.
    const field = rawField.startsWith("reasoning") ? "text" : rawField;
    const delta = String(p.delta ?? "");
    if (!messageID || !partID || (field !== "text" && field !== "output")) return prev;

    const msgIdx = copy.findIndex((m) => m.info.id === messageID);
    if (msgIdx === -1) {
      // Mensagem ainda não anunciada: cria entrada mínima para o delta não se perder.
      const created: SessionMessage = {
        info: { id: messageID, sessionID, role: "assistant" },
        parts: [minimalPart(sessionID, messageID, partID, delta, rawField.startsWith("reasoning") ? "reasoning" : "text")],
      };
      return [...copy, created];
    }
    const msg = { ...copy[msgIdx], parts: [...copy[msgIdx].parts] };
    const pIdx = msg.parts.findIndex((x) => x.id === partID);
    if (pIdx === -1) {
      msg.parts.push(minimalPart(sessionID, messageID, partID, delta, rawField.startsWith("reasoning") ? "reasoning" : "text"));
    } else {
      const updated = { ...msg.parts[pIdx] } as Part & Record<string, unknown>;
      if (rawField.startsWith("reasoning")) updated.type = "reasoning";
      updated[field] = String(updated[field] ?? "") + delta;
      msg.parts[pIdx] = updated as Part;
    }
    copy[msgIdx] = msg;
    return copy;
  }

  if (messageID) {
    const msgIdx = copy.findIndex((m) => m.info.id === messageID);
    if (msgIdx !== -1) {
      const msg = { ...copy[msgIdx], parts: [...copy[msgIdx].parts] };

      if (ev.type === "message.part.removed") {
        msg.parts = msg.parts.filter((x) => x.id !== partID);
        copy[msgIdx] = msg;
        return copy;
      }

      if (ev.type === "message.part.updated" && part) {
        const pIdx = msg.parts.findIndex((x) => x.id === part.id);
        if (pIdx === -1) {
          msg.parts = [...msg.parts, part];
        } else {
          const merged = { ...msg.parts[pIdx], ...part } as Part;
          // Preserva deltas de streaming já acumulados quando o evento traz texto antigo.
          if (
            (part.type === "text" || part.type === "reasoning") &&
            typeof msg.parts[pIdx].text === "string" &&
            typeof part.text === "string"
          ) {
            merged.text = msg.parts[pIdx].text!.length > part.text.length ? msg.parts[pIdx].text : part.text;
          }
          msg.parts[pIdx] = merged;
        }
        copy[msgIdx] = msg;
        return copy;
      }
    }
  }

  return prev;
}
